import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { trans } from '@spotahome/soyuz-trans/client';

import classNames from 'classnames/bind';

import Price from '../../common/Price';
import Button from '../../../Button';

import LabelItem from '../../../LabelItem';
import Tag from '../../../Tag';
import Chip from '../../../Chip';
import BaseChip from '../../../BaseChip';

import IconGeneric from '../../../IconGeneric';

import { ROOM_SHARED, LISTING_TYPES } from '../../../../data/common/property';

import styles from './HomecardContent.module.scss';

const DAY_FORMAT = 'D';
const DEFAULT_LISTING_TYPE = ROOM_SHARED;
const TRANS_DELIMITER = '_';

const getTranslatedMonth = month => {
  const translatedMonths = trans('pikaday.label.months').split(TRANS_DELIMITER);

  return translatedMonths[month];
};

const parseAvailableFrom = availableFrom => {
  const date = moment(availableFrom);
  const month = date.month();
  const translatedMonth = getTranslatedMonth(month);

  const year = date.year();

  return {
    day: date.format(DAY_FORMAT),
    month: translatedMonth,
    year
  };
};

const HomecardContent = ({
  availableFrom,
  price,
  title,
  type,
  labels,
  chips,
  numberOfBathrooms,
  numberOfBedrooms,
  showAvailableFromYear,
  withTransparentBackground,
  onMoreInfoClick,
  hasDescription
}) => {
  const { day, month, year } = parseAvailableFrom(availableFrom);

  const availableFromDates = {
    __html: trans('homecard.available-from.rebranding', {
      ordinalDate: showAvailableFromYear
        ? `${day} ${month} ${year}`
        : `${day} ${month}`
    })
  };

  const hasLabels = labels.some(label => label.text && label.color);
  const hasChips = chips.length;
  const hasBathrooms = !!numberOfBathrooms;
  const hasBedrooms = !!numberOfBedrooms;

  return (
    <div
      className={classNames(styles['homecard-content'], {
        [styles['homecard-content--with-transparent-background']]:
          withTransparentBackground
      })}
    >
      <div className={styles['homecard-content__header']}>
        <>
          <span
            className={classNames(
              styles['homecard-content__type'],
              styles['homecard-content__type--rebranding-style']
            )}
          >
            {trans(`homecard.type.${type}.small`)}
          </span>

          <span className={styles['homecard-content__separator']}>&#9632;</span>

          <span
            className={classNames(
              styles['homecard-content__available-from'],
              styles['homecard-content__available-from--rebranding-style']
            )}
            dangerouslySetInnerHTML={availableFromDates}
          />

          <p
            className={classNames(
              styles['homecard-content__title'],
              styles['homecard-content__title--rebranding-style']
            )}
          >
            {title}
          </p>
        </>

        {(hasBathrooms || hasBedrooms) && (
          <div className={styles['homecard-content__rooms']}>
            {hasBedrooms && (
              <span>
                <IconGeneric
                  name="single-bed"
                  title={trans('listing.property-title.bedrooms')}
                />
                x{numberOfBedrooms}
              </span>
            )}
            {hasBathrooms && hasBedrooms && (
              <span className={styles['homecard-content__separator']}>
                &#9632;
              </span>
            )}
            {hasBathrooms && (
              <span>
                <IconGeneric
                  name="bathroom"
                  title={trans('listing.property-title.bathrooms')}
                />
                x{numberOfBathrooms}
              </span>
            )}
          </div>
        )}
      </div>

      <div
        className={classNames(
          styles['homecard-content__footer'],
          styles['homecard-content__footer--rebranding-style']
        )}
      >
        <div className={styles['homecard-content__price-container']}>
          <Price
            {...price}
            color="dark"
            className={styles['homecard-content__price']}
          />
          {hasDescription && (
            <Button color="link" onClick={onMoreInfoClick}>
              {trans('homecard.more.info')}
            </Button>
          )}
        </div>
        {hasLabels ? (
          <div className={styles['homecard-content__label-container']}>
            {labels.map(label => (
              <LabelItem
                key={label.text}
                size={label.size || 'xxs'}
                {...label}
              />
            ))}
          </div>
        ) : null}
        {!hasLabels && hasChips ? (
          <div className={styles['homecard-content__label-container']}>
            {chips.map(chip => (
              <Tag type="secondary" key={chip.label} {...chip} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

HomecardContent.propTypes = {
  availableFrom: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]).isRequired,
  price: PropTypes.shape(Price.propTypes),
  title: PropTypes.string.isRequired,
  type: PropTypes.oneOf(LISTING_TYPES),
  isMinimalRoundedCard: PropTypes.bool,
  labels: PropTypes.arrayOf(PropTypes.shape(LabelItem.propTypes)),
  chips: PropTypes.arrayOf(PropTypes.shape(BaseChip.propTypes)),
  numberOfBathrooms: PropTypes.number,
  numberOfBedrooms: PropTypes.number,
  showAvailableFromYear: PropTypes.bool,
  withTransparentBackground: PropTypes.bool,
  onMoreInfoClick: PropTypes.func,
  hasDescription: PropTypes.bool
};

HomecardContent.defaultProps = {
  price: {},
  type: DEFAULT_LISTING_TYPE,
  labels: [],
  chips: [],
  isMinimalRoundedCard: false,
  numberOfBathrooms: 0,
  numberOfBedrooms: 0,
  showAvailableFromYear: false,
  withTransparentBackground: false,
  onMoreInfoClick: () => {},
  hasDescription: false
};

export default HomecardContent;
